import 'cordova-plugin-purchase';
import { IAP_PRODUCT_IDS } from 'features/payments/hooks/useIapState';

const WATCH_DUTY_IAP_PRODUCT_IDS: ReadonlySet<string> = new Set(
  Object.values(IAP_PRODUCT_IDS),
);

/**
 * StoreKit reports the app itself as a transaction on every launch (the
 * `appstore.application` virtual id); it never represents a real purchase.
 * Shared across the IAP flow so the sentinel check lives in one place.
 */
export const isVirtualTransaction = (
  transaction: CdvPurchase.Transaction,
): boolean =>
  transaction.transactionId ===
  CdvPurchase.AppleAppStore.APPLICATION_VIRTUAL_TRANSACTION_ID;

const findActiveWatchDutyTransaction = (
  transaction: CdvPurchase.Transaction,
): { id: string } | undefined => {
  if (
    transaction.state !== CdvPurchase.TransactionState.APPROVED ||
    isVirtualTransaction(transaction)
  ) {
    return undefined;
  }

  return transaction.products?.find((p) =>
    WATCH_DUTY_IAP_PRODUCT_IDS.has(p.id),
  );
};

/**
 * True when StoreKit reports an APPROVED transaction for one of our
 * subscription products (the virtual app-launch transaction is ignored).
 */
export const isApprovedWatchDutyTransaction = (
  transaction: CdvPurchase.Transaction,
): boolean => Boolean(findActiveWatchDutyTransaction(transaction));

/**
 * True when an approved Watch Duty subscription was purchased before
 * `checkoutStartedAt` - i.e. the Apple ID already owned it coming into this
 * checkout. StoreKit replays these as "approved" without charging, so
 * processing one would grant a free membership. A genuine purchase made
 * during checkout is dated at/after `checkoutStartedAt`, so it is never
 * flagged.
 *
 * Fails closed on a missing or invalid `purchaseDate`: this flow is
 * Apple-only, where a real approved purchase always has a valid one (the
 * plugin omits it only for deleted transactions), so an approved Watch Duty
 * transaction without one is treated as a collision rather than risk a free
 * membership.
 */
export const isPreCheckoutSubscriptionTransaction = (
  transaction: CdvPurchase.Transaction,
  checkoutStartedAt: number,
): boolean => {
  if (!isApprovedWatchDutyTransaction(transaction)) {
    return false;
  }

  // `undefined` when absent, `NaN` when the Date is invalid; both fail closed.
  const purchaseTime = transaction.purchaseDate?.getTime();
  if (purchaseTime === undefined || Number.isNaN(purchaseTime)) {
    return true;
  }

  return purchaseTime < checkoutStartedAt;
};
